import * as R from 'ramda';
import React from 'react';

import ActivationList from 'iss/pages/ActivationListPage';
import BatchEvaluationActivator from 'iss/pages/BatchEvaluationActivator';
import ContractSuppliersListPage from 'iss/pages/ContractSuppliersListPage';
import ContractSupplierEditor from 'iss/pages/ContractSupplierEditor';
import ContractSupplierReviewListPage from 'iss/pages/ContractSupplierReviewListPage';
import ContractSupplierReviewEditor from 'iss/pages/ContractSupplierReviewEditor';
import EvaluationActivationEditor from 'iss/pages/EvaluationActivationEditor';
import EvaluationEventEditor from 'iss/pages/EvaluationEventEditor';
import FiilissVisibilityTimes from 'iss/pages/FiilissVisibilityTimes';
import FillEvaluationEditor from 'iss/pages/FillEvaluationEditor';
import FormEditor from 'iss/pages/FormEditor';
import FormList from 'iss/pages/FormList';
import GradingScaleEditor from 'iss/pages/GradingScaleEditor';
import GradingScaleList from 'iss/pages/GradingScaleList';
import LateEventsListPage from 'iss/pages/LateEventsListPage';
import QuestionEditor from 'iss/pages/QuestionEditor';
import QuestionGroupEditor from 'iss/pages/QuestionGroupEditor';
import QuestionGroupList from 'iss/pages/QuestionGroupList';
import QuestionListPage from 'iss/pages/QuestionListPage';
import ReportsPage from 'iss/pages/ReportsPage';
import SystemStatusNotifications from 'iss/pages/SystemStatusNotifications';
import {roles} from 'iss/service/PartyService';
import ExtUserPasswordChangeHintPage from './pages/ExtUserPasswordChangeHintPage';
import ReviewEvaluationPage from './pages/ReviewEvaluationPage';

// USER role is required to eliminate displaying anything when an ext-user logins with a temporary password.
// In that case the user only has the EXT_TEMPORARY_USER role. USER role is included in all other cases.
const IS_ADMIN = (userRoles) => userRoles.includes(roles.ADMIN);
const IS_EXT_ADMIN = (userRoles) => userRoles.includes(roles.EXT_ADMIN);
const IS_NOT_EXT_USER = (userRoles) => userRoles.includes(roles.USER) && !userRoles.includes(roles.EXT_USER);
const IS_NOT_ADMIN = (userRoles) => userRoles.includes(roles.USER) && !userRoles.includes(roles.ADMIN);
const IS_TEMP_EXT_USER = (userRoles) => userRoles.includes(roles.EXT_TEMPORARY_USER);
const IS_SITE_MANAGER_OR_ADMIN = (userRoles) => userRoles.includes(roles.SITE_MANAGER) || userRoles.includes(roles.ADMIN);

function passIdAs(idPropName, Component) {
  return ({match}) => <Component {...{[idPropName]: parseInt(match.params.id, 10)}} />;
}

function passTwoIdsAs(idPropName1, idPropName2, Component) {
  return ({match}) => <Component {...{[idPropName1]: parseInt(match.params.id, 10), [idPropName2]: parseInt(match.params.id2, 10)}} />;
}

function passIdAndStaticPropsAs(idPropName, props, Component) {
  return ({match}) => <Component {...{[idPropName]: parseInt(match.params.id, 10), ...props}} />;
}


const routes = [
  {
    id: 'site-info',
    menuTitle: 'other.siteInfoMenuTitle',
    link: getLinkForSiteAdmin(),
    access: IS_SITE_MANAGER_OR_ADMIN
  },
  {
    id: 'activations',
    menuTitle: 'navigation.ownActivations',
    path: "/activatedEvaluations",
    access: IS_NOT_ADMIN,
    component: ActivationList
  },
  {
    id: 'activations',
    menuTitle: 'navigation.allActivations',
    path: "/activatedEvaluations",
    access: IS_ADMIN,
    component: ActivationList
  },
  {
    id: 'contract-suppliers',
    menuTitle: 'contractSuppliers.title',
    path: "/contract-suppliers",
    access: IS_NOT_EXT_USER,
    component: ContractSuppliersListPage
  },
  {
    id: 'late-events',
    menuTitle: 'lateEventsList.title',
    path: "/lateEvents",
    access: IS_NOT_EXT_USER,
    component: LateEventsListPage
  },
  {
    id: 'edit-activation',
    path: "/activatedEvaluations/:id",
    render: passIdAs("activationId", EvaluationActivationEditor)
  },
  {
    id: 'batch-activation',
    menuTitle: 'batchEvaluationActivator.menuTitle',
    path: "/batch-activate-evaluation-form",
    access: IS_NOT_EXT_USER,
    component: BatchEvaluationActivator
  },
  {
    id: 'forms',
    menuTitle: 'formList.title',
    path: "/forms",
    access: IS_NOT_EXT_USER,
    component: FormList
  },
  {
    id: 'new-form',
    path: "/forms/new",
    access: IS_NOT_EXT_USER,
    component: FormEditor
  },
  {
    id: 'edit-form',
    path: "/forms/:id",
    access: IS_NOT_EXT_USER,
    render: passIdAs("formId", FormEditor)
  },
  {
    id: 'questions',
    menuTitle: 'questionList.title',
    path: "/questions",
    access: IS_ADMIN,
    component: QuestionListPage
  },
  {
    id: 'edit-question',
    path: "/questions/:id",
    access: IS_ADMIN,
    render: passIdAs("questionId", QuestionEditor)
  },
  {
    id: 'question-groups',
    path: '/question-groups',
    access: IS_ADMIN,
    component: QuestionGroupList
  },
  {
    id: 'new-question-group',
    path: "/question-groups/new",
    access: IS_ADMIN,
    component: QuestionGroupEditor
  },
  {
    id: 'edit-question-group',
    path: "/question-group/:id",
    access: IS_ADMIN,
    render: passIdAs("groupId", QuestionGroupEditor)
  },
  {
    id: 'grading-scales',
    menuTitle: 'gradingScaleList.title',
    path: "/scales",
    access: IS_NOT_EXT_USER,
    component: GradingScaleList
  },
  {
    id: 'new-grading-scale',
    path: "/scales/new",
    access: IS_NOT_EXT_USER,
    component: GradingScaleEditor
  },
  {
    id: 'new-grading-scale-from-old',
    path: "/scales/new-from/:id",
    access: IS_NOT_EXT_USER,
    component: passIdAndStaticPropsAs("scaleId", {newFromOld: true}, GradingScaleEditor)
  },
  {
    id: 'edit-grading-scale',
    path: "/scales/:id",
    access: IS_NOT_EXT_USER,
    render: passIdAs("scaleId", GradingScaleEditor)
  },
  {
    id: 'edit-event',
    path: "/event/:id",
    render: passIdAs("eventId", EvaluationEventEditor)
  },
  {
    id: 'system-status',
    menuTitle: 'systemStatusNotifications.title',
    path: "/system-status",
    access: IS_ADMIN,
    component: SystemStatusNotifications
  },
  {
    id: 'fiiliss-visibility',
    menuTitle: 'fiilissVisibility.title',
    path: "/fiiliss-visibility",
    access: () => false /* hidden, to be deleted? */,
    component: FiilissVisibilityTimes
  },
  {
    id: 'reports',
    menuTitle: 'navigation.reports',
    path: "/reports",
    access: IS_ADMIN,
    component: ReportsPage
  },
  {
    id: 'ext-users',
    menuTitle: 'other.extUsersMenuTitle',
    link: getLinkForAdminUI(),
    access: IS_EXT_ADMIN // Applies to ext-org admins and super admins.
  },
  {
    id: 'fill-evaluation',
    path: "/fill-evaluation/form/:id/event/:id2",
    render: passTwoIdsAs("formId", "eventId", FillEvaluationEditor)
  },
  {
    id: 'review-evaluation',
    path: "/review-evaluation/form/:id/event/:id2",
    render: passTwoIdsAs("formId", "eventId", ReviewEvaluationPage)
  },
  {
    id: 'new-contract-supplier',
    path: "/contract-supplier/new",
    access: IS_ADMIN,
    component: ContractSupplierEditor
  },
  {
    id: 'edit-contract-supplier',
    path: '/contract-supplier/:id',
    access: IS_NOT_EXT_USER,
    render: passIdAs("supplierId", ContractSupplierEditor)
  },
  {
    id: 'contract-supplier-reviews',
    path: '/contract-supplier/:id/reviews',
    access: IS_NOT_EXT_USER,
    render: passIdAs("supplierId", ContractSupplierReviewListPage)
  },
  {
    id: 'new-contract-supplier-review',
    path: '/contract-supplier/:id/reviews/new',
    access: IS_NOT_EXT_USER,
    render: passIdAs("supplierId", ContractSupplierReviewEditor)
  },
  {
    id: 'edit-contract-supplier-review',
    path: '/contract-supplier/:id/reviews/edit',
    access: IS_NOT_EXT_USER,
    render: passIdAs("supplierId", ContractSupplierReviewEditor)
  },
  {
    id: 'ext-user-password-change',
    path: '/expired-password',
    access: IS_TEMP_EXT_USER,
    component: ExtUserPasswordChangeHintPage
  }
];

export function getRoutes(party) {
  return R.filter(route => {
    return route.access == undefined || route.access(party.roles);
  }, routes);
}

export const home = '/activatedEvaluations';
export const expiredPassword = '/expired-password';

export default getRoutes;

export function getLinkForAdminUI() {
  const hostname = window.location.hostname;
  const isLocal = hostname.match(/localhost/) != null;

  if (isLocal) {
    return 'http://localhost:3001/ext/';
  } else {
    return `https://${hostname}/ext/`;
  }
}

export function getLinkForSiteAdmin() {
  const hostname = window.location.hostname;
  const isLocal = hostname.match(/localhost/) != null;

  if (isLocal) {
    return 'http://localhost:3002/kohdekansiot/';
  } else {
    return `https://${hostname}/kohdekansiot/`;
  }
}



// WEBPACK FOOTER //
// ./modules/issFrontend/routes.js